import { ClockIcon, DocumentDuplicateIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline'
import { UserSearch } from 'components/UserSearch'
import { LinkDirectory } from 'config'
import { useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import { Tooltip } from 'stories/components'
import type { Color } from 'stories/types'
import { InputConvert, MAX_INPUT_DATE, MIN_INPUT_DATE } from 'utils'
import { getTextWidth } from 'utils/textWidth'

const COMPLETE_DATE_RE = /^\d{4}-\d{2}-\d{2}$/

/**
 * Native date inputs emit a full date on the first year digit (10/02/2026 becomes 0002-10-02, then
 * 0020, 0202, and only then 2026). Those intermediate years are real values, so a parent that saves
 * on change persists 0002 and can disable the field before the rest of the year is typed.
 */
const isCompleteInputDate = (value: string): boolean =>
  value === '' || (COMPLETE_DATE_RE.test(value) && value >= MIN_INPUT_DATE && value <= MAX_INPUT_DATE)

interface InputProps {
  autoComplete?: boolean
  /**
   * What background color to use
   */
  bgColor?: string
  color?: Color
  /**
   * Is Full
   */
  full?: boolean
  /**
   * Is disabled
   */
  disabled?: boolean
  /**
   * Is readOnly
   */
  readOnly?: boolean
  /**
   * Tooltip of Input
   */
  tooltip?: string
  /**
   * Is autofocus
   */
  autoFocus?: boolean
  /**
   * Type of Input
   */
  type?: string
  /**
   * Title of Input
   */
  title?: string
  /**
   * Placeholder of Input
   */
  placeholder?: string
  /**
   * Name of Input
   */
  name?: string
  /**
   * Value of Input
   */
  value?: string
  /**
   * Font Size of Input
   */
  fontSize?: number
  /**
   * Error of Input
   */
  error?: string
  /**
   * Custom class name
   */
  className?: string
  /**
   * Is has icon
   */
  hasIcon?: boolean
  /**
   * Is has icon
   */
  required?: boolean
  /**
   * Icon component
   */
  icon?: string | JSX.Element | null
  /**
   * Prefix
   */
  prefix?: string
  /**
   * onChange
   */
  history?: boolean

  additionalElements?: JSX.Element | Function | null

  onBlurNull?: boolean

  copyable?: boolean

  userSearch?: boolean

  transparent?: boolean

  indent?: string | number | null

  options?: string[]

  showLinkDir?: boolean

  decimalStep?: number

  isHyperLink?: boolean

  onKeyDown?: (e: any) => void

  onChange: (e: any, additionals?: any) => void // string | React.ChangeEvent<HTMLInputElement>) => void
  onBlur?: (value?: any) => void
  showHistory?: () => void
  onKeyPress?: (e: any) => void
  onFocus?: (e: any) => void
  onKeyUp?: (e: any) => void
  onClick?: (e: any) => void
}

/**
 * Primary UI component for user interaction
 */
export const Input2 = ({
  autoComplete = false,
  bgColor = '',
  disabled = false,
  readOnly = false,
  tooltip = '',
  autoFocus = false,
  type = 'text',
  title = '',
  placeholder = ' ',
  name = '',
  value = '',
  fontSize = 15,
  error = '',
  className = '',
  hasIcon = false,
  icon = null,
  prefix = '',
  history = false,
  required = false,
  additionalElements = null,
  onBlurNull = true,
  copyable = false,
  userSearch = false,
  transparent = false,
  indent = null,
  options = [],
  showLinkDir = false,
  isHyperLink = false,
  onChange = () => {},
  onBlur = () => {},
  onFocus,
  onKeyDown,
  showHistory = () => {},
  decimalStep,
  ...props
}: InputProps) => {
  const width = getTextWidth(title)

  const classNames = [
    'block',
    'rounded-xl',
    'px-3',
    'pb-2',
    width < window.innerWidth - 20 ? 'pt-[27px]' : 'pt-[45px]',
    'w-full',
    `text-[${fontSize}px]`,
    'text-gray-900',
    // soft-filled (Material-3): tinted at rest, turns white on focus
    bgColor ? `bg-${bgColor}` : transparent ? 'bg-transparent' : disabled ? 'bg-slate-100' : 'bg-white',
    readOnly ? 'cursor-not-allowed' : '',
    readOnly ? 'border-none font-variation-settings-600' : 'border border-slate-300',
    'transition',
    readOnly || disabled ? '' : 'hover:border-slate-400',
    'focus:outline-none',
    'focus:bg-white',
    'focus:border-shade-blue',
    'focus:ring-2',
    'focus:ring-shade-blue/20',
    'peer',
    prefix.length > 0 && `pl-${indent || 7}`,
    hasIcon && 'pl-9',
    error && 'border-rose-400',
    disabled && 'cursor-not-allowed',
    type === 'color' && 'h-[55px]',
  ]
  if (value === null) value = ''
  value = InputConvert({ type, decimalStep }, value)

  const inputRef = useRef<HTMLInputElement>(null)
  /** True after a keystroke, until blur. A calendar pick does not set it. */
  const dateFromKeyboard = useRef(false)

  const [showPassword, setShowPassword] = useState(false)
  const [isFocused, setIsFocused] = useState(false)
  /** Date typed but not yet echoed by the parent. Keeps the control from snapping back mid-save. */
  const [heldDate, setHeldDate] = useState<string | null>(null)

  useEffect(() => {
    if (heldDate !== null && (value || '') === heldDate) setHeldDate(null)
  }, [heldDate, value])

  // A date field stays uncontrolled. Pushing each keystroke back through `value` resets Chrome's
  // month/day/year segments, so typing 10022026 never lands. The parent hears only a finished date.
  useEffect(() => {
    if (type !== 'date' || !inputRef.current) return
    if (document.activeElement === inputRef.current) return
    if (heldDate !== null && (value || '') !== heldDate) return
    const next = value || ''
    if (inputRef.current.value !== next) inputRef.current.value = next
  }, [heldDate, type, value])
  /**
   * True while an empty date field should stand in as a text input (see the input below). Never
   * once it holds a value, and never while it is focused, so editing and the picker are untouched.
   *
   * Has to sit below `isFocused`: read above its `const`, an empty date field threw a temporal-dead-zone
   * ReferenceError that took the whole page down with it, since nothing here is under an error boundary.
   */
  const renderDateAsText = type === 'date' && !value && !heldDate && !isFocused
  const isPassword = type === 'password'

  const publishDate = (next: string, additionals: Record<string, string> = {}) => {
    if ((next || '') === (value || '') || heldDate === next) return
    setHeldDate(next)
    onChange(next, additionals)
  }

  const changeInput = (e: any, additionals: Record<string, string> = {}) => {
    let _value = e.target.value
    if (type.toLowerCase() === 'email') _value = _value.trim().toLowerCase()
    if (type === 'date') {
      // A filled date turns "05" into 05/02/2026 immediately. That is still the middle of typing,
      // so save it on blur instead of disabling the field. A calendar pick has no keystroke.
      if (e.target.validity?.badInput || !isCompleteInputDate(_value) || dateFromKeyboard.current) return
      publishDate(_value, additionals)
    } else onChange(_value, additionals)
    if (!onBlurNull) {
      setTimeout(() => {
        if (document.activeElement === inputRef.current) {
          if (inputRef?.current?.value === InputConvert({ type }, _value)) {
            inputRef?.current?.blur()
          }
        }
      }, 5000)
    }
  }

  const copyValue = () => {
    toast(`'${title}' Copied!`, { type: 'info' })
    navigator.clipboard.writeText(value)
  }

  const hasOptions = !!options.length

  return (
    <div className="input-container">
      <div className={`group relative z-0 w-full ${className}`}>
        {hasIcon && icon && typeof icon === 'string' && (
          <div className="flex absolute bottom-2 left-0 items-center pl-3 pointer-events-none">
            <img src={icon} className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          </div>
        )}
        {hasIcon && icon && typeof icon !== 'string' && (
          <div className="flex absolute bottom-2 left-0 items-center pl-3 pointer-events-none">{icon}</div>
        )}

        {prefix.length > 0 && (
          <div className="text-gray-600 text-[14px] flex absolute top-[29px] left-0 items-center pl-3 pointer-events-none">
            {prefix}
          </div>
        )}
        <input
          /*
           * An empty date field renders as TEXT, showing an mm/dd/yyyy placeholder, and becomes a
           * real date input the moment it is focused.
           *
           * Browsers disagree about what an empty <input type="date"> looks like: Chrome and
           * Firefox show "mm/dd/yyyy", Safari fills it with TODAY'S DATE in grey. A servicer
           * looking at Safari sees a date sitting in Mortgage Acquisition Date with no way to tell
           * it from one that was keyed. Hiding the native text with CSS does not work reliably -
           * Safari draws the date as separate month/day/year sub-fields and a colour set on their
           * container never reaches them - so the type swap sidesteps the native rendering
           * altogether and gives every browser the same answer.
           */
          type={renderDateAsText ? 'text' : isPassword && showPassword ? 'text' : type}
          name={name}
          id={name}
          list={hasOptions ? `${name}-list` : ''}
          className={classNames.filter(Boolean).join(' ')}
          placeholder={renderDateAsText ? 'mm/dd/yyyy' : placeholder}
          disabled={disabled}
          autoFocus={autoFocus}
          ref={inputRef}
          required={required}
          onChange={changeInput}
          onKeyDown={(e: any) => {
            if (type === 'date') {
              dateFromKeyboard.current = true
              if (e.key === 'Enter') {
                const next = e.currentTarget.value || ''
                if (!e.currentTarget.validity?.badInput && isCompleteInputDate(next)) publishDate(next)
              }
            }
            onKeyDown && onKeyDown(e)
          }}
          onFocus={(e: any) => {
            setIsFocused(true)
            // Changing `type` re-creates the native control, so put focus back on it. Without this
            // the first click on an empty date field would land nowhere.
            if (renderDateAsText) setTimeout(() => inputRef.current?.focus(), 0)
            onFocus && onFocus(e)
          }}
          onBlur={(event: any) => {
            setIsFocused(false)
            if (type === 'date') {
              const keyed = dateFromKeyboard.current
              dateFromKeyboard.current = false
              const typed = event.target.value || ''
              if (event.target.validity?.badInput || !isCompleteInputDate(typed)) {
                event.target.value = value || ''
                onBlur && onBlur(value)
                return
              }
              if (keyed) {
                // Publish first so the parent can dispatch; defer onBlur a tick so parents that
                // gate saves on React state (setFieldValueChanged) see the dirty flag after
                // onChange has run to its first await. Same-turn onBlur used to skip the PUT.
                publishDate(typed)
                queueMicrotask(() => onBlur && onBlur(typed))
                return
              }
            }
            onBlur && onBlur(event.target.value)
          }}
          readOnly={readOnly}
          autoComplete={hasOptions || !autoComplete ? 'off' : ''}
          style={{ paddingLeft: typeof indent === 'string' ? indent : '' }}
          // Bounded to the same window InputValidate accepts, so a mis-keyed two-digit year
          // (8/26/26 -> 0026-08-26) is refused by the field rather than silently carried.
          min={type === 'date' ? MIN_INPUT_DATE : undefined}
          max={type === 'date' ? MAX_INPUT_DATE : undefined}
          {...(type === 'date' ? { defaultValue: value || '' } : { value })}
          {...props}
        />
        {isPassword && (
          <button
            type="button"
            className="absolute bottom-2 right-2 text-gray-500 hover:text-gray-700"
            onClick={() => setShowPassword((prev) => !prev)}
            tabIndex={-1}
          >
            {showPassword ? <EyeSlashIcon className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
          </button>
        )}
        {hasOptions && (
          <datalist id={`${name}-list`}>
            {options.map((county, index) => (
              <option key={index} value={county} />
            ))}
          </datalist>
        )}
        <label
          htmlFor={name}
          className="absolute text-[11px] font-medium text-slate-500 top-2 z-10 origin-[0] left-3 flex gap-2 items-center max-w-[calc(100%-2rem)]"
        >
          <span className="truncate" title={title}>
            {title}
          </span>
          {required && '*'}
          {tooltip.length > 0 ? <Tooltip message={tooltip}></Tooltip> : null}
          {showLinkDir && (
            <select
              name={`show-Link-Directory`}
              id={`show-Link-Directory`}
              className="py-0 pl-1 pr-6 text-[12.5px] leading-[16px] border-gray-300"
              onChange={(e) => onChange(LinkDirectory[e.target.value])}
            >
              <option disabled value="" selected>
                -- Link Directory --
              </option>
              {Object.keys(LinkDirectory).map((key) => {
                return (
                  <option value={key} key={key}>
                    {key}
                  </option>
                )
              })}
            </select>
          )}
          {additionalElements !== null &&
            (typeof additionalElements == 'function' ? additionalElements(onChange) : additionalElements)}
          {isHyperLink && value && (
            <a
              href={value as string}
              target="_blank"
              rel="noopener noreferrer"
              className="ml-2 text-shade-blue hover:underline cursor-pointer text-[12px]"
              onClick={(e) => e.stopPropagation()}
            >
              🔗 Open Link
            </a>
          )}
          {history && (
            <span className="ml-1 hidden group-hover:inline" onClick={() => showHistory()}>
              <ClockIcon className="h-[14px] w-[14px] text-gray-600 cursor-pointer" aria-hidden="true" />
            </span>
          )}
          {copyable && (
            <span className="ml-1 hidden group-hover:inline hover-shadow1" onClick={() => copyValue()}>
              <DocumentDuplicateIcon className="h-[14px] w-[14px] text-gray-600 cursor-pointer" aria-hidden="true" />
            </span>
          )}
        </label>
      </div>
      {userSearch && (
        <div className="">
          <UserSearch
            query={value}
            onSelect={(value: string, user: Record<string, string>) => changeInput({ target: { value } }, user)}
          />
        </div>
      )}
      {error && (
        <p className={`${readOnly && 'ml-2'} peer-invalid:visible text-rose-700 text-[13px] pt-[1px] pl-1`}>{error}</p>
      )}
    </div>
  )
}
